import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Loading } from '@/components/Loading';
import { paymentAuditApi } from '@/lib/api/payment-audit';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { getAppFormatLocale } from '@/utils/locale';

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(getAppFormatLocale(), { dateStyle: 'short', timeStyle: 'medium' });
}

export function PaymentsAuditTab() {
  const { t } = useTranslation();

  const query = useInfiniteQuery({
    queryKey: ['payment-audit-events'],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      paymentAuditApi.list({ limit: 30, cursor: pageParam as string | undefined }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const rows = query.data?.pages.flatMap((p) => p.items) ?? [];

  if (query.isLoading) {
    return <Loading />;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t('payments.audit.description')}</p>
      {!rows.length ? (
        <p className="text-sm text-muted-foreground">{t('payments.audit.empty')}</p>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('payments.audit.columns.when')}</TableHead>
                <TableHead>{t('payments.audit.columns.event')}</TableHead>
                <TableHead>{t('payments.audit.columns.actor')}</TableHead>
                <TableHead>{t('payments.audit.columns.detail')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="whitespace-nowrap text-xs">{formatDateTime(row.createdAt)}</TableCell>
                  <TableCell className="text-xs font-mono">{row.eventType}</TableCell>
                  <TableCell className="text-xs">
                    {row.actorEmail ?? row.actorUserId ?? '-'}
                  </TableCell>
                  <TableCell className="max-w-md text-sm">{row.detail ?? '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {query.hasNextPage ? (
            <Button
              variant="outline"
              size="sm"
              disabled={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
            >
              {query.isFetchingNextPage ? t('payments.audit.loadingMore') : t('payments.audit.loadMore')}
            </Button>
          ) : null}
        </>
      )}
    </div>
  );
}
